import React from "react";
import * as Icons from "lucide-react";
import { api, apiError } from "@/lib/api";
import { RagBadge } from "@/components/common/RagBadge";
import { DecideActions } from "@/components/people/DecideActions";
import { fmtDay } from "@/lib/format";
import { LEAVE_STATUS, requestWhen, requestSize, fmtTime } from "@/lib/people";
import { toast } from "sonner";

export function LeaveDecisionRow({ r, onChanged, showStatus = false }) {
  const decide = async (decision, note) => {
    try {
      await api.post(`/workspace/leave/requests/${r.id}/decide`, { decision, note: note || null });
      toast.success(decision === "approve" ? "Time off approved" : "Request rejected — they've been told why");
      onChanged();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const st = LEAVE_STATUS[r.status];
  return (
    <div className="amden-card flex flex-col gap-3 p-4 lg:flex-row lg:items-center" data-testid={`leave-row-${r.id}`}>
      <span className="hidden h-10 w-1.5 shrink-0 rounded-full lg:block" style={{ background: r.colour }} />
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold text-[#0B1729]">{r.user_name} · <span data-testid={`leave-type-${r.id}`}>{r.type_name}</span></div>
        <div className="text-xs text-[#1E2D44]/55">{requestWhen(r)} · {requestSize(r)}{r.reason ? ` · “${r.reason}”` : ""}</div>
        {r.masked && <div className="mt-0.5 flex items-center gap-1 text-[11px] text-[#1E2D44]/40"><Icons.Lock size={10} /> Details visible to people administrators only</div>}
      </div>
      {showStatus && <RagBadge rag={st.rag}>{st.label}</RagBadge>}
      {r.status === "pending" && r.can_decide !== false && <DecideActions onDecide={decide} testPrefix={`leave-${r.id}`} />}
      {r.status === "pending" && r.can_decide === false && <span className="text-xs text-[#1E2D44]/45">With {r.approver_name || "approver"}</span>}
    </div>
  );
}

export function CorrectionDecisionRow({ c, tz, onChanged }) {
  const decide = async (decision, note) => {
    try {
      await api.post(`/workspace/clock/corrections/${c.id}/decide`, { decision, note: note || null });
      toast.success(decision === "approve" ? "Correction applied to the time record" : "Correction rejected");
      onChanged();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  return (
    <div className="amden-card flex flex-col gap-3 p-4 lg:flex-row lg:items-center" data-testid={`correction-row-${c.id}`}>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold text-[#0B1729]">{c.user_name} · {fmtDay(c.date)}</div>
        <div className="text-xs text-[#1E2D44]/60">
          {c.entry_id ? <>Was {fmtTime(c.current_clock_in, tz)} – {c.current_clock_out ? fmtTime(c.current_clock_out, tz) : "still open"} → </> : "Missing record: "}
          <b className="text-[#0B1729]">{fmtTime(c.proposed_clock_in, tz)} – {c.proposed_clock_out ? fmtTime(c.proposed_clock_out, tz) : "open"}</b>
        </div>
        <div className="text-xs text-[#1E2D44]/55">“{c.reason}”</div>
      </div>
      <DecideActions onDecide={decide} testPrefix={`correction-${c.id}`} />
    </div>
  );
}
